import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Clock, TrendingUp, ThumbsUp, MessageCircle, Play, Loader2 } from "lucide-react";
import { api, timeAgo, truncate } from "@/lib/api";
import Seo from "@/components/Seo";

const SORT_OPTIONS = [
  { id: "latest", label: "Latest", icon: Clock },
  { id: "most_answered", label: "Most Answered", icon: MessageCircle },
  { id: "most_upvoted", label: "Most Upvoted", icon: ThumbsUp },
];

const CATEGORIES = [
  { id: "", label: "All" },
  { id: "Ask Advice", label: "Advice" },
  { id: "Relationship", label: "Relationships" },
  { id: "Work / Stress", label: "Work & Stress" },
  { id: "Vent / Confess", label: "Vent" },
  { id: "Casual", label: "Casual" },
];

function QuestionCard({ question, onClick }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef(null);
  
  const hasVideo = question.video_urls?.length > 0;
  const hasImage = question.image_urls?.length > 0;
  const hasMedia = hasVideo || hasImage;
  
  // Get full URL for media
  const getFullUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  };
  
  const mediaUrl = hasVideo 
    ? getFullUrl(question.video_urls[0]) 
    : (hasImage ? getFullUrl(question.image_urls[0]) : null);

  const toggleVideo = (e) => {
    e.stopPropagation();
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  return (
    <Card 
      className="group overflow-hidden border-slate-700/50 bg-slate-800/50 backdrop-blur-sm transition-all duration-300 hover:border-indigo-500/50 hover:shadow-lg hover:shadow-indigo-500/10 hover:-translate-y-1 cursor-pointer"
      onClick={onClick}
    >
      {/* Media */}
      {hasMedia && (
        <div className="relative aspect-[16/9] w-full overflow-hidden bg-slate-900">
          {hasVideo ? (
            <>
              <video
                ref={videoRef}
                src={mediaUrl}
                className="h-full w-full object-cover"
                loop
                muted
                playsInline
              />
              {!isPlaying && (
                <button
                  onClick={toggleVideo}
                  className="absolute inset-0 flex items-center justify-center bg-black/40"
                >
                  <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white/20 backdrop-blur-sm transition-transform group-hover:scale-110">
                    <Play className="ml-1 h-6 w-6 text-white" fill="currentColor" />
                  </div>
                </button>
              )}
              <Badge className="absolute top-3 right-3 bg-red-500/90 text-white border-0">VIDEO</Badge>
            </>
          ) : (
            <img src={mediaUrl} alt="" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
          )}
        </div>
      )}

      {/* Content */}
      <div className="p-4">
        {/* Category Badge */}
        <Badge className="mb-2 rounded-full bg-indigo-500/20 px-3 py-1 text-xs font-medium text-indigo-300 border-0">
          {question.intent || "General"}
        </Badge>
        
        <h3 className="font-semibold text-slate-100 line-clamp-2 group-hover:text-white transition-colors">
          {question.title}
        </h3>
        <p className="mt-1.5 text-sm text-slate-400 line-clamp-2">{question.body_preview}</p>
        
        {/* Tags */}
        <div className="mt-2 flex flex-wrap gap-1">
          {question.tags?.slice(0, 3).map((tag) => (
            <Badge key={tag} className="rounded bg-slate-700/50 text-slate-400 text-[10px] border-0">
              #{tag}
            </Badge>
          ))}
        </div>

        {/* Meta */}
        <div className="mt-3 flex items-center gap-3 text-xs text-slate-500">
          <span>{timeAgo(question.created_at)}</span>
          <span className="flex items-center gap-1">
            <MessageCircle className="h-3 w-3" />
            {question.answer_count} answers
          </span>
          {question.top_answer_score > 0 && (
            <span className="flex items-center gap-1 text-emerald-400">
              <ThumbsUp className="h-3 w-3" />
              {question.top_answer_score}
            </span>
          )}
        </div>

        {/* Top Answer */}
        {question.top_answer_preview && (
          <div className="mt-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20 p-2.5">
            <div className="text-[10px] font-medium text-emerald-400 mb-1">Top Answer</div>
            <p className="text-xs text-emerald-300/80 line-clamp-2">
              {truncate(question.top_answer_preview, 100)}
            </p>
          </div>
        )}
      </div>
    </Card>
  );
}

export default function Browse() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState(searchParams.get("sort") || "latest");
  const [intent, setIntent] = useState(searchParams.get("intent") || "");

  const load = async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ sort, page: "1", limit: "30" });
      if (intent) qs.set("intent", intent);
      const { data } = await api.get(`/feed?${qs.toString()}`);
      setItems(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const params = new URLSearchParams();
    if (sort !== "latest") params.set("sort", sort);
    if (intent) params.set("intent", intent);
    setSearchParams(params);
  }, [sort, intent]);

  return (
    <div data-testid="feed-page" className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-900 to-slate-950">
      <div className="mx-auto max-w-5xl px-4 py-6">
        <Seo title="Browse - AskIncog" description="Browse anonymous questions." />

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-100">Browse Questions</h1>
          <p className="text-sm text-slate-500 mt-1">Explore what Bangalore is talking about</p>
        </div>

        {/* Sort Buttons */}
        <div className="mb-4 flex gap-2 overflow-x-auto pb-2">
          {SORT_OPTIONS.map((opt) => (
            <Button
              key={opt.id}
              variant={sort === opt.id ? "default" : "outline"}
              size="sm"
              onClick={() => setSort(opt.id)}
              className={`flex items-center gap-1.5 ${
                sort === opt.id 
                  ? "bg-indigo-600 text-white hover:bg-indigo-500 border-0" 
                  : "bg-slate-800/50 text-slate-400 hover:bg-slate-800 hover:text-slate-200 border-slate-700"
              }`}
            >
              <opt.icon className="h-4 w-4" />
              {opt.label}
            </Button>
          ))}
        </div>

        {/* Category Filter */}
        <div className="mb-6 flex gap-2 overflow-x-auto pb-2">
          {CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setIntent(cat.id)}
              className={`flex-shrink-0 rounded-full px-4 py-1.5 text-sm font-medium transition-all ${
                intent === cat.id
                  ? "bg-indigo-600 text-white"
                  : "bg-slate-800/50 text-slate-400 hover:bg-slate-800 hover:text-slate-300 border border-slate-700/50"
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Questions Grid */}
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
          </div>
        ) : items.length === 0 ? (
          <Card className="border-slate-700/50 bg-slate-800/50 p-12 text-center">
            <MessageCircle className="mx-auto h-12 w-12 text-slate-600 mb-4" />
            <h3 className="text-lg font-medium text-slate-300 mb-2">No questions found</h3>
            <p className="text-slate-500">Try a different category or sort option</p>
          </Card>
        ) : (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((q, i) => (
              <div key={q.slug} className="animate-fadeInUp" style={{ animationDelay: `${i * 0.03}s` }}>
                <QuestionCard
                  question={q}
                  onClick={() => navigate(`/q/${q.slug}`)}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
